import React, { useCallback, useEffect, useState } from "react"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Chip from "@mui/material/Chip"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import MenuItem from "@mui/material/MenuItem"
import Pagination from "@mui/material/Pagination"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import moment from "moment"
import PageContent from "components/common/LayoutComponents/PageContent"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import LoadingScreen from "components/common/LoadingScreen"
import { UsePermissionVerification } from "components/customHooks"
import { PERMISSIONS } from "constants/permission"
import {
  approveDsarRequest,
  cancelDsarRequest,
  createDsarRequest,
  executeDsarErase,
  getDsarRequestDetail,
  listDsarRequests,
} from "services/memberDsar"
import { verifyResponse } from "utils/httpRequest"
import { StyledMemberDsar } from "./MemberDsar.style"

const STATUS_OPTIONS = ["", "pending", "approved", "in_progress", "completed", "failed", "cancelled"]

const statusColor = {
  pending: "warning",
  approved: "info",
  in_progress: "primary",
  completed: "success",
  failed: "error",
  cancelled: "default",
}

const MemberDsar = () => {
  const [loading, setLoading] = useState(false)
  const [requests, setRequests] = useState([])
  const [pageInfo, setPageInfo] = useState(null)
  const [filters, setFilters] = useState({ pageNo: 1, pageSize: 20, searchEmail: "", requestStatus: "" })
  const [createForm, setCreateForm] = useState({ memberEmail: "", memberId: "", requestNotes: "" })
  const [detail, setDetail] = useState(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const AccessControl = UsePermissionVerification()

  useEffect(() => {
    AccessControl.SetAccess({
      ...AccessControl.Access,
      dsarListAccess: AccessControl.VerifyAccess([PERMISSIONS.MEMBER.MEMBER_DSAR_LIST]),
      dsarCreateAccess: AccessControl.VerifyAccess([PERMISSIONS.MEMBER.MEMBER_DSAR_CREATE]),
      dsarDetailAccess: AccessControl.VerifyAccess([PERMISSIONS.MEMBER.MEMBER_DSAR_DETAIL]),
      dsarApproveAccess: AccessControl.VerifyAccess([PERMISSIONS.MEMBER.MEMBER_DSAR_APPROVE]),
      dsarExecuteAccess: AccessControl.VerifyAccess([PERMISSIONS.MEMBER.MEMBER_DSAR_EXECUTE_ERASE]),
      dsarCancelAccess: AccessControl.VerifyAccess([PERMISSIONS.MEMBER.MEMBER_DSAR_CANCEL]),
    })
  }, [AccessControl.UserPermissions])

  const loadRequests = useCallback(async () => {
    if (!AccessControl.VerifyAccess([PERMISSIONS.MEMBER.MEMBER_DSAR_LIST])) return
    setLoading(true)
    try {
      const payload = {
        pageNo: filters.pageNo,
        pageSize: filters.pageSize,
        searchEmail: filters.searchEmail || undefined,
        requestStatus: filters.requestStatus || undefined,
        requestType: "erase",
      }
      const response = await listDsarRequests(payload)
      verifyResponse(response)
      setRequests(response.data.list || [])
      setPageInfo(response.data)
    } finally {
      setLoading(false)
    }
  }, [filters, AccessControl.UserPermissions])

  useEffect(() => {
    loadRequests()
  }, [loadRequests])

  const refreshDetail = async (id) => {
    const response = await getDsarRequestDetail({ id })
    verifyResponse(response)
    setDetail(response.data)
    setDetailOpen(true)
  }

  const createRequest = async () => {
    if (!createForm.memberEmail.trim()) return
    setLoading(true)
    try {
      const response = await createDsarRequest({
        memberEmail: createForm.memberEmail.trim(),
        memberId: createForm.memberId ? parseInt(createForm.memberId, 10) : undefined,
        requestType: "erase",
        requestSource: "cms",
        requestNotes: createForm.requestNotes,
      })
      verifyResponse(response)
      setCreateForm({ memberEmail: "", memberId: "", requestNotes: "" })
      setFilters({ ...filters, pageNo: 1 })
      await loadRequests()
    } finally {
      setLoading(false)
    }
  }

  const runAction = async (action, id) => {
    setLoading(true)
    try {
      const response = await action({ id })
      verifyResponse(response)
      await loadRequests()
      if (detailOpen) await refreshDetail(id)
    } finally {
      setLoading(false)
    }
  }

  const renderActions = (request) => (
    <div className="row-actions">
      {AccessControl.Access?.dsarDetailAccess && (
        <Button size="small" variant="outlined" onClick={() => refreshDetail(request.id)}>
          Detail
        </Button>
      )}
      {AccessControl.Access?.dsarApproveAccess && ["pending", "failed"].includes(request.requestStatus) && (
        <Button size="small" variant="contained" onClick={() => runAction(approveDsarRequest, request.id)}>
          Approve
        </Button>
      )}
      {AccessControl.Access?.dsarExecuteAccess && ["approved", "failed"].includes(request.requestStatus) && (
        <Button size="small" color="error" variant="contained" onClick={() => runAction(executeDsarErase, request.id)}>
          Execute Erase
        </Button>
      )}
      {AccessControl.Access?.dsarCancelAccess && ["pending", "approved", "failed"].includes(request.requestStatus) && (
        <Button size="small" color="inherit" variant="outlined" onClick={() => runAction(cancelDsarRequest, request.id)}>
          Cancel
        </Button>
      )}
    </div>
  )

  return (
    <PageContent>
      <StyledMemberDsar>
        <PageHeading>
          <div className="heading-copy">
            <h1 className="page-title">DSAR / Erase Member</h1>
            <p className="page-subtitle">
              Track verified privacy requests, approve account erasure, and retain an auditable step-by-step report.
            </p>
          </div>
        </PageHeading>

        {!AccessControl.Access?.dsarListAccess && (
          <div className="page-empty">You do not have permission to view DSAR requests.</div>
        )}

        {AccessControl.Access?.dsarListAccess && (
          <div className="content-stack">
            {AccessControl.Access?.dsarCreateAccess && (
              <Card className="section-card">
                <CardContent>
                  <h3 className="section-title">Create Erasure Request</h3>
                  <div className="form-grid">
                    <TextField
                      label="Member email"
                      value={createForm.memberEmail}
                      onChange={(event) => setCreateForm({ ...createForm, memberEmail: event.target.value })}
                      size="small"
                      required
                    />
                    <TextField
                      label="Member ID (optional)"
                      value={createForm.memberId}
                      onChange={(event) => setCreateForm({ ...createForm, memberId: event.target.value })}
                      size="small"
                    />
                    <TextField
                      label="Request notes"
                      value={createForm.requestNotes}
                      onChange={(event) => setCreateForm({ ...createForm, requestNotes: event.target.value })}
                      size="small"
                      multiline
                      minRows={2}
                    />
                    <Button variant="contained" onClick={createRequest}>
                      Create Request
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="section-card">
              <CardContent>
                <div className="filters">
                  <TextField
                    label="Email or member ID"
                    value={filters.searchEmail}
                    onChange={(event) => setFilters({ ...filters, searchEmail: event.target.value, pageNo: 1 })}
                    size="small"
                  />
                  <TextField
                    label="Status"
                    value={filters.requestStatus}
                    onChange={(event) => setFilters({ ...filters, requestStatus: event.target.value, pageNo: 1 })}
                    select
                    size="small"
                  >
                    {STATUS_OPTIONS.map((status) => (
                      <MenuItem key={status || "all"} value={status}>
                        {status || "All"}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button variant="outlined" onClick={loadRequests}>
                    Refresh
                  </Button>
                </div>

                <TableContainer component={Paper}>
                  <Table aria-label="member dsar table">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Member ID</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Due</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {requests.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="table-empty">
                            No DSAR requests found.
                          </TableCell>
                        </TableRow>
                      )}
                      {requests.map((request) => (
                        <TableRow key={request.id}>
                          <TableCell>{request.id}</TableCell>
                          <TableCell>{request.memberEmail}</TableCell>
                          <TableCell>{request.memberId || "-"}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={request.requestStatus}
                              color={statusColor[request.requestStatus] || "default"}
                            />
                          </TableCell>
                          <TableCell>{request.dueAt ? moment(request.dueAt).format("ll") : "-"}</TableCell>
                          <TableCell>{request.createdAt ? moment(request.createdAt).format("lll") : "-"}</TableCell>
                          <TableCell align="right">{renderActions(request)}</TableCell>
                        </TableRow>
                      ))}
                      {pageInfo && pageInfo.pages > 1 && (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <div className="pagination-section">
                              <Pagination
                                count={pageInfo.pages}
                                page={pageInfo.currentPageNo}
                                onChange={(event, value) => setFilters({ ...filters, pageNo: value })}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </div>
        )}

        <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>DSAR Request Detail</DialogTitle>
          <DialogContent dividers>
            {detail?.request && (
              <div className="detail-stack">
                <Typography variant="subtitle1">
                  Request #{detail.request.id} · {detail.request.memberEmail} · {detail.request.requestStatus}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Result summary
                </Typography>
                <pre>{JSON.stringify(detail.request.resultSummary || {}, null, 2)}</pre>
                <Typography variant="body2" color="text.secondary">
                  Retention and external actions
                </Typography>
                <pre>{JSON.stringify({
                  retainedSummary: detail.request.retainedSummary || {},
                  externalActions: detail.request.externalActions || {},
                }, null, 2)}</pre>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Step</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Target</TableCell>
                        <TableCell>Rows</TableCell>
                        <TableCell>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(detail.logs || []).map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>{log.stepKey}</TableCell>
                          <TableCell>{log.stepType}</TableCell>
                          <TableCell>{log.stepStatus}</TableCell>
                          <TableCell>{log.targetName}</TableCell>
                          <TableCell>{log.affectedRows}</TableCell>
                          <TableCell>
                            <pre>{JSON.stringify(log.details || {}, null, 2)}</pre>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </StyledMemberDsar>
      <LoadingScreen loading={loading} />
    </PageContent>
  )
}

export default MemberDsar
